import { useCallback, useEffect, useState } from 'react';

import { profileService } from '../services/profileService';
import type {
  Profile,
  ProfileCreatePayload,
  ProfileUpdatePayload,
} from '../types/profiles';

interface UseProfilesReturn {
  profiles: Profile[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  create: (payload: ProfileCreatePayload) => Promise<Profile>;
  update: (id: string, payload: ProfileUpdatePayload) => Promise<Profile>;
  remove: (id: string, reassignTo?: string | null) => Promise<void>;
}

export function useProfiles(): UseProfilesReturn {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await profileService.list();
      setProfiles(data);
    } catch (e) {
      setError(e as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(
    async (payload: ProfileCreatePayload) => {
      const p = await profileService.create(payload);
      await refresh();
      return p;
    },
    [refresh]
  );

  const update = useCallback(
    async (id: string, payload: ProfileUpdatePayload) => {
      const p = await profileService.update(id, payload);
      await refresh();
      return p;
    },
    [refresh]
  );

  const remove = useCallback(
    async (id: string, reassignTo?: string | null) => {
      await profileService.remove(id, reassignTo);
      await refresh();
    },
    [refresh]
  );

  return { profiles, isLoading, error, refresh, create, update, remove };
}
